import React from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'

import useInfiniteOrders from 'src/flp/donations/useInfiniteOrders'

import Donations from './Donations'

// Modal listing all donations to the active fundraiser. Shares
// state with Goal: the parent fetches `orders` + `more_orders_available`
// every 30s; useInfiniteOrders takes those as initial seed and
// merges new top-of-list rows when the prop changes (its top-visible
// effect detects when the first order id has shifted forward and
// either uses the new prop directly or fetches the diff if the new
// list isn't contiguous with the displayed one). Bottom-visible
// loads the next page via fetchDonations.
//
// Goal drives the openPortalModal lifecycle and hands the resolved
// `portal` (target + close fn) in; we portal the modal-header + list
// into the target. We don't pass an explicit IntersectionObserver
// root — `scrollWithin: true` on the openPortalModal call makes the
// iframe document the scroll container (mode 2) or the .widget-modal
// dialog (mode 1/3), and the spec-default root (target's
// owner-document viewport) coincides with that scroll container in
// both modes, so intersections fire correctly without us specifying.

const DonorFeed = ({ portal, i18n, orders, moreOrdersAvailable, feedUrl }) => {
  const { orders: visible, loading, refs } = useInfiniteOrders({
    orders,
    more_orders_available: moreOrdersAvailable,
    feedUrl,
  })

  return createPortal(
    <>
      <button
        type="button"
        className="widget-modal-close hide-in-blank"
        aria-label={i18n.t('close')}
        onClick={() => portal.close()}
      />
      <div className="px-3 py-4 chrome:px-4 chrome:py-5">
        <h2 className="text-xl font-semibold mb-2">
          {i18n.t('goal.recent_donations')}
        </h2>
        {/* Top sentinel for the new-orders intersection check. */}
        <div ref={refs.top}></div>
        <Donations i18n={i18n} orders={visible} />
        {/* Bottom sentinel: when scrolled into view, useInfiniteOrders
            fetches the next page. Spinner only visible while a page
            is in flight; the empty div still serves as the sentinel
            when not loading. */}
        <div
          ref={refs.bottom}
          className="text-center -mt-4 -mb-5 text-gray-400"
          style={{ opacity: loading ? 1 : 0, transition: 'opacity 0.1s' }}
        >
          <FontAwesomeIcon icon={faSpinner} className="fa-spin-pulse" />
        </div>
      </div>
    </>,
    portal.target,
  )
}

export default DonorFeed
